import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import Dialog from "~/components/Dialog/Dialog";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import { DialogLayout } from "~/components/DialogLayout/DialogLayout";
import ExternalLink from "~/components/Navigation/ExternalLink";
import { AddProjectTriggerCard } from "./AddProjectTriggerCard";
import { BuiltInFeedIcon } from "./Icons/BuiltInFeedIcon";
import { ContainerImageIcon } from "./Icons/ContainerImageIcon";
import { DeploymentIcon } from "./Icons/DeploymentIcon";
import { HelmIcon } from "./Icons/HelmIcon";
import { ScheduledIcon } from "./Icons/ScheduledIcon";
interface AddProjectTriggerDialogLayoutProps {
    closeDialog(): void;
    project: ProjectResource;
}
function AddProjectTriggerDialogLayout({ closeDialog, project }: AddProjectTriggerDialogLayoutProps) {
    const { SpaceId: spaceId, Slug: projectSlug } = project;
    const canCreateBuiltInFeedTrigger = !project.AutoCreateRelease && !project.IsVersionControlled;
    const builtInFeedTriggerDisabledMessage = project.IsVersionControlled ? "Built-in feed triggers are not supported in version controlled projects" : "This trigger type only supports one trigger per project";
    const cancel = <Button key="Cancel" label="Cancel" onClick={() => closeDialog()} importance={"quiet"}/>;
    return (<div className={styles.wrapper}>
            <DialogLayout actions={[cancel]} title={<h2 className={styles.title}>Select trigger type</h2>} extraHeaderContent={() => (<div className={styles.description}>
                        Triggers allow you to create, deploy or promote releases based on specific events or schedules. Get started by selecting the type of trigger you want to create. Learn more about&nbsp;
                        <ExternalLink href="ProjectTriggers">triggers</ExternalLink>
                    </div>)} closeDialog={closeDialog}>
                <div className={styles.cardList}>
                    <AddProjectTriggerCard title="Helm chart" description="Create a release when new Helm charts are published to an external feed." icon={<HelmIcon />} path={links.createReleaseCreationFeedTriggerPage.generateUrl({ spaceId, projectSlug })}/>
                    <AddProjectTriggerCard title="Container image" description="Create a release when new container images are published to an external feed." icon={<ContainerImageIcon />} path={links.createReleaseCreationFeedTriggerPage.generateUrl({ spaceId, projectSlug })}></AddProjectTriggerCard>
                    <AddProjectTriggerCard title="Built-in package repository" description="Create a release when a package is pushed to the built-in package repository." icon={<BuiltInFeedIcon />} path={links.createBuiltInFeedTriggerPage.generateUrl({ spaceId, projectSlug })} disabled={!canCreateBuiltInFeedTrigger} disabledMessage={builtInFeedTriggerDisabledMessage}></AddProjectTriggerCard>
                    <AddProjectTriggerCard title="Deployment target" description="Trigger a deployment when machine-related events occur." icon={<DeploymentIcon />} path={links.createDeploymentTriggerPage.generateUrl({ spaceId, projectSlug })}></AddProjectTriggerCard>
                    <AddProjectTriggerCard title="Scheduled" description="Create, deploy, and promote releases on a defined schedule." icon={<ScheduledIcon />} path={links.createDeploymentScheduledTriggerPage.generateUrl({ spaceId, projectSlug })}></AddProjectTriggerCard>
                </div>
            </DialogLayout>
        </div>);
}
const styles = {
    wrapper: css({
        padding: space[16],
        paddingBottom: space[12], // Account for slightly less padding around actions in DialogLayout
    }),
    title: css({ font: text.interface.heading.medium, margin: 0 }),
    description: css({ font: text.interface.body.default.base, marginBottom: space[24] }),
    cardList: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[16],
    }),
};
interface AddProjectTriggerDialogButtonProps {
    project: ProjectResource;
}
export function AddProjectTriggerDialogButton({ project }: AddProjectTriggerDialogButtonProps) {
    return (<OpenDialogButton label="Add Trigger" renderDialog={({ open, closeDialog }) => (<Dialog open={open} dialogWidth="800px">
                    <AddProjectTriggerDialogLayout closeDialog={closeDialog} project={project}/>
                </Dialog>)}/>);
}
